import { Box, Flex, Text } from '@chakra-ui/react'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'

import { useReportingI18n } from '../i18n'
import type { ExtensionData } from '../types'

interface ExtensionComparisonTableProps {
  data: ExtensionData[]
  grandTotalCalls: number
  grandTotalMinutes: number
}

const PercentageBar = ({ percentage }: { percentage: number }) => {
  return (
    <Flex alignItems="center" gap="8px">
      <Text
        {...textStyles[textStyleKeys.Body3]}
        color="fg.default"
        flexShrink={0}
        minWidth="42px"
      >
        {percentage.toFixed(1)}%
      </Text>
      <Box
        width="100%"
        maxWidth="50%"
        height="8px"
        bg="bg.muted"
        borderRadius="full"
        overflow="hidden"
      >
        <Box
          width={`${percentage}%`}
          height="100%"
          bg="brand.solid"
          borderRadius="full"
          transition="width 0.3s ease"
        />
      </Box>
    </Flex>
  )
}

const ExtensionComparisonTable = ({
  data,
  grandTotalCalls,
  grandTotalMinutes,
}: ExtensionComparisonTableProps) => {
  const { text } = useReportingI18n()

  const cellTextStyle = textStyles[textStyleKeys.Body3]

  const totalCalls = grandTotalCalls
  const totalMinutes = grandTotalMinutes

  return (
    <Box
      width="100%"
      border="1px solid"
      borderColor="border.default"
      borderRadius="10px"
      overflow="hidden"
    >
      <Flex
        padding="8px 16px"
        alignItems="center"
        gap="16px"
        borderBottom="1px solid"
        borderColor="border.default"
        bg="bg.subtle"
      >
        <Box flex="1 0 0" padding="4px 0">
          <Text {...cellTextStyle} color="fg.default">
            {text('extensionComparisonTable.columns.extension', 'Extension')}
          </Text>
        </Box>
        <Box flex="0.7 0 0" padding="4px 0">
          <Text {...cellTextStyle} color="fg.default">
            {text('extensionComparisonTable.columns.calls', 'Calls')}
          </Text>
        </Box>
        <Box flex="1.3 0 0" padding="4px 0">
          <Text {...cellTextStyle} color="fg.default">
            {text('extensionComparisonTable.columns.percentCalls', '% Total')}
          </Text>
        </Box>
        <Box flex="0.7 0 0" padding="4px 0">
          <Text {...cellTextStyle} color="fg.default">
            {text('extensionComparisonTable.columns.minutes', 'Minutes')}
          </Text>
        </Box>
        <Box flex="1.3 0 0" padding="4px 0">
          <Text {...cellTextStyle} color="fg.default">
            {text('extensionComparisonTable.columns.percentMinutes', '% Total')}
          </Text>
        </Box>
      </Flex>

      <Box>
        {data.map((row, index) => {
          const callPercent =
            totalCalls > 0 ? (row.totalCalls / totalCalls) * 100 : 0
          const minutePercent =
            totalMinutes > 0 ? (row.totalMinutes / totalMinutes) * 100 : 0

          return (
            <Flex
              key={row.extension}
              padding="8px 16px"
              alignItems="center"
              gap="16px"
              borderBottom={index < data.length - 1 ? '1px solid' : 'none'}
              borderColor="border.default"
              _hover={{ bg: 'bg.subtle' }}
            >
              <Box flex="1 0 0" padding="4px 0">
                <Text {...cellTextStyle} color="fg.default">
                  {row.displayLabel}
                </Text>
              </Box>
              <Box flex="0.7 0 0" padding="4px 0">
                <Text {...cellTextStyle} color="fg.default">
                  {row.totalCalls.toLocaleString()}
                </Text>
              </Box>
              <Box flex="1.3 0 0" padding="4px 0">
                <PercentageBar percentage={callPercent} />
              </Box>
              <Box flex="0.7 0 0" padding="4px 0">
                <Text {...cellTextStyle} color="fg.default">
                  {row.totalMinutes.toLocaleString()}
                </Text>
              </Box>
              <Box flex="1.3 0 0" padding="4px 0">
                <PercentageBar percentage={minutePercent} />
              </Box>
            </Flex>
          )
        })}
      </Box>
    </Box>
  )
}

export default ExtensionComparisonTable
